import React, { useEffect, useRef, useState } from 'react';
import Editor from '@monaco-editor/react';
import {
	Alert,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Divider,
	Snackbar,
	Typography,
} from '@mui/material';
import { getYml, patchYml } from '../../Services/connectorAccount';
import { useParams } from 'react-router';

export const ConnectorAccountYml = () => {
	const editorRef = useRef(null);
	const { projectFolderId, sku, accountId } = useParams();

	const [ymlData, setYmlData] = React.useState<string | undefined>(undefined);
	const [isPending, setIsPending] = React.useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState<boolean>(false);
	const [isDirty, setIsDirty] = useState<boolean>(false);
	const [success, setSuccess] = useState<string | null>(null);

	const handleEditorDidMount: (editor: any, monaco: any) => void = (editor) => {
		editorRef.current = editor;
	};

	const handleEditorChange: (value: string | undefined, event: any) => void = (
		value,
		event
	) => {
		setIsDirty(true);
		setYmlData(value);
	};

	const loadYmlData = async () => {
		if (!projectFolderId || !sku || !accountId) return;

		setIsPending(true);

		try {
			const r = await getYml(projectFolderId, sku, accountId); // doit renvoyer une string YAML
			setYmlData(r ?? '');
		} catch (e: any) {
			setError(e?.message || 'Erreur lors du chargement du YAML');
			setYmlData('');
		} finally {
			setIsPending(false);
		}
	};

	const handleSave = async () => {
		if (!projectFolderId || !sku || !accountId) return;
		setSaving(true);
		setError(null);
		setSuccess(null);
		try {
			await patchYml(projectFolderId, sku, accountId, { yml: ymlData }); // doit renvoyer une string YAML
			setSuccess('YAML sauvegardé avec succès');
		} catch (e: any) {
			setError(e?.message || 'Erreur lors du chargement du YAML');
			setYmlData('');
		} finally {
			setSaving(false);
		}
	};
	const handleCancel = () => {};

	useEffect(() => {
		loadYmlData();
	}, [projectFolderId, sku, accountId]);

	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Yml
				</Typography>
				{isPending && <Typography>Loading...</Typography>}
				{error && <Typography color="error">{error}</Typography>}
				{!isPending && (
					<Editor
						height="80vh"
						defaultLanguage="yaml"
						theme="vs-dark"
						value={ymlData}
						onMount={handleEditorDidMount}
						onChange={handleEditorChange}
						options={{
							minimap: { enabled: false },
							wordWrap: 'on',
							automaticLayout: true,
						}}
					/>
				)}
			</CardContent>
			<Box sx={{ position: 'sticky', bottom: 0, zIndex: 1 }}>
				<Divider />
				<CardActions
					sx={{
						justifyContent: 'flex-end',
						bgcolor: 'background.paper',
						position: 'relative',
					}}
				>
					<Button onClick={handleCancel} disabled={!isDirty || saving}>
						Annuler
					</Button>
					<Button onClick={handleSave} disabled={!isDirty}>
						Sauvegarder
					</Button>
				</CardActions>
			</Box>
			<Snackbar
				open={!!success}
				autoHideDuration={3000}
				onClose={() => setSuccess(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setSuccess(null)}
					severity="success"
					variant="filled"
				>
					{success}
				</Alert>
			</Snackbar>
		</Card>
	);
};
